/**
 * driverDocs — the driver file, and what each slot in it says about itself.
 *
 * THE GHANA CARD NUMBER IS A BLANK FILL-IN LINE ON THE INSTRUMENT, NOT A MERGE FIELD. No
 * ID-number column exists anywhere in the schema (`driver_identification` is id, timestamps,
 * driver_id, document_url, expires_at, type), so these two documents feed nothing and gate
 * nothing. They are records — which is exactly why their copy must never carry urgency, and why
 * `expired` is the only state here allowed to go destructive.
 *
 * `today` IS AN ARGUMENT, NOT A HIDDEN CLOCK. Whether a licence is expired is the one derivation
 * on this canvas that changes answer overnight, so it is passed in and pinned by tests rather than
 * read from the machine inside a component.
 *
 * PURE, AND REACT-FREE — vitest runs node-env over `.ts` only.
 */

import { format, isBefore, parseISO, startOfDay } from 'date-fns';

import { businessDay } from '@/lib/format';

/** One `driver_identification` row. */
export interface DriverDocument {
    id: number;
    type: string;
    document_url: string;
    expires_at: string | null;
}

/** The two document slots the driver file draws, in the order it draws them. */
export const DRIVER_DOCUMENT_TYPES: { type: string; label: string }[] = [
    { type: 'DRIVING_LICENCE', label: 'Driving licence' },
    { type: 'NATIONAL_ID', label: 'Ghana Card' },
];

export interface DocumentStatus {
    present: boolean;
    expired: boolean;
    /** The slot's own line: `On file · expires Mar 3, 2027`, `Expired Jan 2, 2026`, `Not on file`. */
    meta: string;
}

/**
 * What one slot says at rest.
 *
 * A DOCUMENT WITH NO EXPIRY IS ON FILE, NOT SUSPECT. `expires_at` is nullable and most Ghana Cards
 * are recorded without one; saying "no expiry recorded" is a fact, and treating it as a problem
 * would put a destructive mark on the majority of a file that gates nothing.
 */
export function documentStatus(
    document: DriverDocument | undefined,
    today: Date = new Date(),
): DocumentStatus {
    if (!document?.document_url) return { present: false, expired: false, meta: 'Not on file' };
    if (!document.expires_at) {
        return { present: true, expired: false, meta: 'On file · no expiry recorded' };
    }
    /*
     * THE DAY TURNS IN ACCRA, NOT ON THE VIEWER'S CLOCK — and only the `now` side was wrong.
     * `expires_at` is a `date` column, so `parseISO('2027-03-03')` is already the day the registry
     * wrote, at local midnight, and the line below prints that same value; leave it alone.
     * `startOfDay(today)` was the defect: it read the HOST's calendar day, so from UTC+14 a licence
     * expiring TODAY in Ghana compared as expired — the destructive mark on this canvas, raised a
     * day early, over a date the row itself still prints as the expiry.
     */
    const expired = isBefore(parseISO(document.expires_at), businessDay(today) ?? startOfDay(today));
    return {
        present: true,
        expired,
        meta: `${expired ? 'Expired' : 'Expires'} ${format(parseISO(document.expires_at), 'MMM d, yyyy')}`,
    };
}

export function findDocument(
    documents: DriverDocument[],
    type: string,
): DriverDocument | undefined {
    return documents.find((row) => row.type === type);
}

/**
 * `4 of 6 recorded` — the one number the manifest's collapsed Driver file row carries.
 *
 * SIX, AND THE SAME SIX EVERYWHERE. The count is licence, Ghana Card, date of birth, photograph,
 * email and repayment tag; a second definition of "recorded" anywhere would make the manifest and
 * the canvas disagree about a number they print side by side.
 */
export function driverFileCount(
    documents: DriverDocument[],
    profile: { birth_date?: string | null; photo?: string | null; email?: string | null },
    driver: { repayment_tag?: string | null },
    today: Date = new Date(),
): { recorded: number; total: number } {
    const recorded = [
        documentStatus(findDocument(documents, 'DRIVING_LICENCE'), today).present,
        documentStatus(findDocument(documents, 'NATIONAL_ID'), today).present,
        !!profile.birth_date,
        !!profile.photo,
        !!profile.email,
        !!driver.repayment_tag,
    ].filter(Boolean).length;
    return { recorded, total: 6 };
}
